/**
 * Pure math helpers for spirometry waveform statistics.
 * All functions operate on values in Litres (L) and L/s — callers are
 * responsible for converting from the mL/mL-s units used in the API types.
 */

import type { SpirometryEffortCurve } from "../types";

const ML_PER_L = 1000;

/** Volume grid resolution for mean-curve computation (0.01 L = 10 mL). */
const GRID_STEP_L = 0.01;

/**
 * Linearly interpolate `flows` (sampled at `volumes`) onto a new `grid` of
 * volume values. Out-of-range grid points are clamped to the nearest
 * endpoint value.
 *
 * `volumes` must be monotonically non-decreasing (spirometry expiratory
 * phase typically is, after any start-of-breath adjustment).
 */
export function resampleCurve(
  volumes: number[],
  flows: number[],
  grid: number[],
): number[] {
  if (volumes.length === 0) return grid.map(() => 0);
  const lastIdx = volumes.length - 1;

  return grid.map((gridVol) => {
    if (gridVol <= volumes[0]) return flows[0];
    if (gridVol >= volumes[lastIdx]) return flows[lastIdx];

    // Binary search for the left bracket index.
    let lo = 0;
    let hi = lastIdx;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (volumes[mid] <= gridVol) lo = mid;
      else hi = mid;
    }

    const span = volumes[hi] - volumes[lo];
    if (span === 0) return flows[lo];
    const t = (gridVol - volumes[lo]) / span;
    return flows[lo] + t * (flows[hi] - flows[lo]);
  });
}

/**
 * Element-wise mean of a set of equal-length curves.
 * Returns an empty array when `curves` is empty.
 */
export function averageCurves(curves: number[][]): number[] {
  if (curves.length === 0) return [];
  const n = curves[0].length;
  const count = curves.length;
  return Array.from({ length: n }, (_, i) => {
    let sum = 0;
    for (const curve of curves) sum += curve[i] ?? 0;
    return sum / count;
  });
}

/**
 * Element-wise sample standard deviation of a set of equal-length curves
 * around pre-computed `means`. Uses n-1 (Bessel's correction).
 * Returns zeros when fewer than 2 curves are provided.
 */
export function sdCurves(curves: number[][], means: number[]): number[] {
  if (curves.length < 2) return means.map(() => 0);
  const n = means.length;
  return Array.from({ length: n }, (_, i) => {
    let sumSq = 0;
    for (const curve of curves) sumSq += ((curve[i] ?? 0) - means[i]) ** 2;
    return Math.sqrt(sumSq / (curves.length - 1));
  });
}

/** Time offset (ms) at which FEV1 is measured — the volume exhaled in 1 second. */
const FEV1_TIME_MS = 1000;

/**
 * Linearly interpolate a monotonically-increasing `timeMs` → `values` series at
 * `targetMs`. Out-of-range targets clamp to the nearest endpoint.
 */
export function interpolateAtTime(
  timeMs: number[],
  values: number[],
  targetMs: number,
): number {
  const lastIdx = timeMs.length - 1;
  if (lastIdx < 0) return 0;
  if (targetMs <= timeMs[0]) return values[0];
  if (targetMs >= timeMs[lastIdx]) return values[lastIdx];

  let lo = 0;
  let hi = lastIdx;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (timeMs[mid] <= targetMs) lo = mid;
    else hi = mid;
  }

  const span = timeMs[hi] - timeMs[lo];
  if (span === 0) return values[lo];
  const t = (targetMs - timeMs[lo]) / span;
  return values[lo] + t * (values[hi] - values[lo]);
}

/** Derived spirometry measures for a single effort, in clinical units. */
export interface SpirometryMeasures {
  /** Forced vital capacity — total exhaled volume (L). */
  fvc: number;
  /** Forced expiratory volume in the first second (L). */
  fev1: number;
  /** Peak expiratory flow (L/s). */
  pef: number;
}

/**
 * Derive FVC, FEV1 and PEF from one effort's flow-volume/volume-time curve.
 *
 * The curve arrives as parallel `time_ms`, `flow_ml` (mL/s) and `volume_ml` (mL)
 * arrays where `volume_ml` is the cumulative exhaled volume (monotonically
 * non-decreasing). Volumes are baseline-corrected against the first sample so a
 * non-zero start does not inflate FVC/FEV1.
 *
 * Returns `null` when the effort has no usable samples.
 */
export function deriveEffortMeasures(
  effort: SpirometryEffortCurve,
): SpirometryMeasures | null {
  const { time_ms: timeMs, flow_ml: flowMl, volume_ml: volumeMl } = effort;
  if (timeMs.length === 0 || volumeMl.length === 0) return null;

  const baselineMl = volumeMl[0];

  let maxVolumeMl = -Infinity;
  for (const v of volumeMl) if (v > maxVolumeMl) maxVolumeMl = v;
  const fvc = (maxVolumeMl - baselineMl) / ML_PER_L;

  const volumeAt1sMl = interpolateAtTime(timeMs, volumeMl, FEV1_TIME_MS);
  const fev1 = (volumeAt1sMl - baselineMl) / ML_PER_L;

  let maxFlowMl = 0;
  for (const f of flowMl) if (f > maxFlowMl) maxFlowMl = f;
  const pef = maxFlowMl / ML_PER_L;

  return { fvc, fev1, pef };
}

/** Best-of-visit spirometry measures, including the FEV1/FVC ratio. */
export interface VisitMeasures extends SpirometryMeasures {
  /** FEV1/FVC expressed as a percentage (0–100). */
  fev1FvcRatio: number;
}

/**
 * Reduce all of a visit's efforts to a single best-of-visit measure set, per the
 * ATS/ERS convention: the largest FVC, FEV1 and PEF are taken independently
 * (they may come from different acceptable efforts), and FEV1/FVC is computed
 * from those best values.
 *
 * Returns `null` when no effort yields usable measures.
 */
export function bestVisitMeasures(
  efforts: SpirometryEffortCurve[],
): VisitMeasures | null {
  const perEffort = efforts
    .map(deriveEffortMeasures)
    .filter((m): m is SpirometryMeasures => m !== null);
  if (perEffort.length === 0) return null;

  const fvc = Math.max(...perEffort.map((m) => m.fvc));
  const fev1 = Math.max(...perEffort.map((m) => m.fev1));
  const pef = Math.max(...perEffort.map((m) => m.pef));
  const fev1FvcRatio = fvc > 0 ? (fev1 / fvc) * 100 : 0;

  return { fvc, fev1, pef, fev1FvcRatio };
}

/**
 * Compute the mean flow-volume curve from a set of spirometry effort curves.
 *
 * Each effort is resampled onto a shared volume grid running from 0 to the
 * maximum observed FVC at 0.01 L steps. The returned volumes/flows are in
 * Litres / (L/s).
 *
 * Returns `{ volumes: [], flows: [] }` when `efforts` is empty.
 */
export function computeMeanCurve(efforts: SpirometryEffortCurve[]): {
  volumes: number[];
  flows: number[];
} {
  if (efforts.length === 0) return { volumes: [], flows: [] };

  // Convert to Litres.
  const effortsL = efforts.map((e) => ({
    volumes: e.volume_ml.map((v) => v / ML_PER_L),
    flows: e.flow_ml.map((f) => f / ML_PER_L),
  }));

  // Shared volume grid: 0 → max observed FVC.
  const maxFvc = Math.max(
    ...effortsL.map((e) => (e.volumes.length ? Math.max(...e.volumes) : 0)),
  );
  if (maxFvc <= 0) return { volumes: [], flows: [] };

  const steps = Math.ceil(maxFvc / GRID_STEP_L);
  const grid = Array.from({ length: steps + 1 }, (_, i) => i * GRID_STEP_L);

  // Resample each effort onto the shared grid and average.
  const resampled = effortsL.map((e) =>
    resampleCurve(e.volumes, e.flows, grid),
  );
  const flows = averageCurves(resampled);

  return { volumes: grid, flows };
}
